import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  customerOrders, supplierOrders,
  type CustomerOrderListItem, type SupplierOrderListItem,
  type OrderKind,
} from '@/api/orders';
import { contractors as contractorsApi } from '@/api/catalogs';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

type Kind = 'customer' | 'supplier';
type Row = CustomerOrderListItem | SupplierOrderListItem;
type KindFilter = 'ALL' | OrderKind;

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATE_LABEL: Record<string, string> = {
  OPEN: 'Відкритий',
  IN_PROGRESS: 'У роботі',
  COMPLETED: 'Виконаний',
  CANCELLED: 'Скасований',
};

export default function OrdersPage({
  kind,
  forcedOrderKind,
  title: titleOverride,
  newButtonLabel,
}: {
  kind: Kind;
  /** ПРОДАЖ-розбивка Етап 1 (2026-06-03): якщо задано — UI без kind-фільтра, всі записи цього виду. */
  forcedOrderKind?: OrderKind;
  title?: string;
  newButtonLabel?: string;
}) {
  const navigate = useNavigate();
  const isCustomer = kind === 'customer';
  const baseRoute = isCustomer ? '/customer-orders' : '/supplier-orders';
  const defaultTitle = isCustomer ? 'Замовлення покупців' : 'Замовлення постачальникам';
  const title = titleOverride ?? defaultTitle;

  const [kindFilter, setKindFilter] = useState<KindFilter>(
    forcedOrderKind ?? 'ALL',
  );
  // ПРОДАЖ-розбивка Етап 5 (2026-06-03): default — тільки відкриті.
  const [closingFilter, setClosingFilter] = useState<'OPEN' | 'CLOSED' | 'ALL'>('OPEN');

  const list = useQuery({
    queryKey: [
      isCustomer ? 'customer-orders' : 'supplier-orders',
      { kindFilter, closingFilter },
    ],
    queryFn: () => {
      const params = {
        limit: 200,
        ...(kindFilter !== 'ALL' ? { order_kind: kindFilter } : {}),
        ...(closingFilter !== 'ALL' ? { closing_status: closingFilter } : {}),
      };
      return isCustomer
        ? customerOrders.list(params)
        : supplierOrders.list(params);
    },
  });

  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.list({ limit: 1000 }),
  });

  const contractorName = useMemo(() => {
    const m = new Map<string, string>();
    contractorsQ.data?.items.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [contractorsQ.data]);

  const cols = useMemo<ColumnDef<Row>[]>(() => [
    { key: 'document_number', header: '№', width: 150, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
    {
      key: 'document_date', header: 'Дата', width: 170,
      sortValue: (r) => r.document_date ?? '',
      cell: (r) => r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : '',
    },
    {
      key: 'contractor_id', header: isCustomer ? 'Покупець' : 'Постачальник',
      sortValue: (r) => contractorName.get(r.contractor_id) ?? '',
      cell: (r) => contractorName.get(r.contractor_id) ?? '',
    },
    {
      key: 'delivery_date', header: 'Термін', width: 120,
      sortValue: (r) => r.delivery_date ?? '',
      cell: (r) => r.delivery_date ? new Date(r.delivery_date).toLocaleDateString('uk-UA') : '',
    },
    {
      key: 'state', header: 'Стан', width: 130,
      sortValue: (r) => STATE_LABEL[r.state] ?? r.state,
      cell: (r) => STATE_LABEL[r.state] ?? r.state,
    },
    // ПРОДАЖ-розбивка Етап 5 (2026-06-03): колонка closing_status.
    {
      key: 'closing_status', header: 'Закриття', width: 110,
      sortValue: (r) => r.closing_status === 'CLOSED' ? '📦 Закрите' : '🟢 Відкрите',
      cell: (r) => (
        <span style={r.closing_status === 'CLOSED'
          ? { color: '#1e40af', fontWeight: 600 }
          : { color: '#16a34a', fontWeight: 400 }}>
          {r.closing_status === 'CLOSED' ? '📦 Закрите' : '🟢 Відкрите'}
        </span>
      ),
    },
    {
      key: 'total_amount', header: 'Сума', width: 140, align: 'right',
      sortValue: (r) => Number(r.total_amount),
      cell: (r) => fmt2(r.total_amount),
    },
    {
      key: 'posted', header: 'Проведено', width: 130, align: 'center',
      sortValue: (r) => r.posted ? 1 : 0,
      cell: (r) => r.posted ? '✓ так' : 'ні',
    },
  ], [contractorName, isCustomer]);

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>{title}</h2>
        {!forcedOrderKind && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <label style={{ fontSize: '0.875rem' }}>Тип:</label>
            <select
              value={kindFilter}
              onChange={(e) => setKindFilter(e.target.value as KindFilter)}
              data-testid="orders-kind-filter"
              style={{ width: 140, flex: '0 0 auto' }}
            >
              <option value="ALL">Усі</option>
              <option value="ORDER">Замовлення</option>
              <option value="PROFORMA_INVOICE">Рахунки</option>
            </select>
          </div>
        )}
        {/* ПРОДАЖ-розбивка Етап 5 (2026-06-03): фільтр closing_status */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label style={{ fontSize: '0.875rem' }}>Закриття:</label>
          <select
            value={closingFilter}
            onChange={(e) => setClosingFilter(e.target.value as 'OPEN' | 'CLOSED' | 'ALL')}
            style={{ width: 140, flex: '0 0 auto' }}
          >
            <option value="OPEN">🟢 Відкриті</option>
            <option value="CLOSED">📦 Закриті</option>
            <option value="ALL">Усі</option>
          </select>
        </div>
        <button
          className="primary"
          style={{ marginLeft: 'auto' }}
          onClick={() => navigate(
            `${baseRoute}/new${forcedOrderKind ? `?kind=${forcedOrderKind}` : ''}`,
          )}
        >
          + {newButtonLabel ?? 'Нове замовлення'}
        </button>
      </div>
      <DataTable<Row>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`${baseRoute}/${r.id}`)}
        rowMuted={(r) => r.deletion_mark ? 0.4 : undefined}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
